/**
 * GoodGuardian — Case Timeline
 *
 * A single chronological feed merging all logged data:
 * custody events, communications, order violations, wellbeing check-ins,
 * calendar events, and court orders.
 *
 * Read-only presentation layer. No new data entry.
 * Export as a court-ready PDF timeline.
 */
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import { printCaseTimeline } from '@/utils/printUtils'

// ── Type filter config ────────────────────────────────────────────────────────
const SOURCES = [
  { id: 'all',       label: 'All',        icon: '🗂️'  },
  { id: 'event',     label: 'Events',     icon: '📋'  },
  { id: 'comm',      label: 'Comms',      icon: '💬'  },
  { id: 'violation', label: 'Violations', icon: '⚠️'  },
  { id: 'wellbeing', label: 'Wellbeing',  icon: '😊'  },
  { id: 'calendar',  label: 'Calendar',   icon: '📅'  },
  { id: 'order',     label: 'Orders',     icon: '⚖️'  },
]

const DATE_RANGES = [
  { id: '30',  label: 'Last 30 days'  },
  { id: '90',  label: 'Last 90 days'  },
  { id: '180', label: 'Last 6 months' },
  { id: 'all', label: 'All time'      },
]

// ── Visual config per sub-type ────────────────────────────────────────────────
const EVENT_BORDER = {
  exchange: 'border-l-blue-400', violation: 'border-l-red-500',
  missed: 'border-l-orange-500', incident: 'border-l-yellow-500',
  medical: 'border-l-purple-400', school: 'border-l-green-400', other: 'border-l-slate-400',
}
const EVENT_BADGE = {
  exchange: 'bg-blue-100 text-blue-700', violation: 'bg-red-100 text-red-700',
  missed: 'bg-orange-100 text-orange-700', incident: 'bg-yellow-100 text-yellow-700',
  medical: 'bg-purple-100 text-purple-700', school: 'bg-green-100 text-green-700',
  other: 'bg-slate-100 text-slate-600',
}
const EVENT_LABEL = {
  exchange: '🔄 Exchange', violation: '🚫 Violation', missed: '❌ Missed Visit',
  incident: '⚠️ Incident', medical: '🏥 Medical', school: '🎓 School', other: '📌 Other',
}

const TONE_BORDER = {
  neutral: 'border-l-slate-400', cooperative: 'border-l-green-400',
  hostile: 'border-l-red-500', concerning: 'border-l-orange-500',
}
const TONE_BADGE = {
  neutral: 'bg-slate-100 text-slate-600', cooperative: 'bg-green-100 text-green-700',
  hostile: 'bg-red-100 text-red-700', concerning: 'bg-orange-100 text-orange-700',
}
const TONE_LABEL = {
  neutral: '😐 Neutral', cooperative: '🤝 Cooperative',
  hostile: '⚠️ Hostile', concerning: '🚩 Concerning',
}

const VIO_BORDER = {
  low: 'border-l-slate-400', medium: 'border-l-amber-400',
  high: 'border-l-orange-500', critical: 'border-l-red-600',
}
const VIO_BADGE = {
  low: 'bg-slate-100 text-slate-600', medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700',
}

const MOOD_META = {
  great: { emoji: '😄', badge: 'bg-green-100 text-green-700'  },
  good:  { emoji: '🙂', badge: 'bg-brand-100 text-brand-700'  },
  okay:  { emoji: '😐', badge: 'bg-slate-100 text-slate-600'  },
  upset: { emoji: '😢', badge: 'bg-amber-100 text-amber-700'  },
  angry: { emoji: '😡', badge: 'bg-orange-100 text-orange-700'},
  sick:  { emoji: '🤒', badge: 'bg-red-100 text-red-600'      },
}

const CAL_BADGE = {
  'custody-exchange': 'bg-blue-100 text-blue-700',
  appointment: 'bg-purple-100 text-purple-700',
  school: 'bg-green-100 text-green-700',
  court: 'bg-red-100 text-red-700',
  compliance: 'bg-brand-100 text-brand-700',
  other: 'bg-slate-100 text-slate-600',
}
const CAL_LABEL = {
  'custody-exchange': '🔄 Exchange', appointment: '🏥 Appointment',
  school: '🎓 School', court: '⚖️ Court Hearing',
  compliance: '📋 Compliance', other: '📌 Other',
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function todayYMD() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function dateLabel(ymd) {
  if (!ymd) return 'Unknown Date'
  const today = todayYMD()
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  const yest = new Date(); yest.setDate(yest.getDate() - 1)
  const yYMD = `${yest.getFullYear()}-${String(yest.getMonth()+1).padStart(2,'0')}-${String(yest.getDate()).padStart(2,'0')}`
  if (ymd === today) return 'Today'
  if (ymd === yYMD)  return 'Yesterday'
  return dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

function cutoffDate(rangeId) {
  if (rangeId === 'all') return null
  const d = new Date()
  d.setDate(d.getDate() - parseInt(rangeId, 10))
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

// ── Normalize all sources → unified item shape ────────────────────────────────
function normalize({ events, comms, violations, wellbeingLogs, calendarEvents, orders, kidProfiles }) {
  const kidMap = Object.fromEntries((kidProfiles || []).map(k => [k.id, k.name]))
  const items = []

  // Custody events
  for (const e of events || []) {
    items.push({
      id: `event-${e.id}`,
      source: 'event',
      date: e.date,
      time: e.time || null,
      sortKey: `${e.date} ${e.time || '23:59'}`,
      icon: '📋',
      title: EVENT_LABEL[e.type] || e.type,
      summary: e.description,
      borderClass: EVENT_BORDER[e.type] || 'border-l-blue-400',
      badgeText: EVENT_LABEL[e.type] || e.type,
      badgeColor: EVENT_BADGE[e.type] || 'bg-slate-100 text-slate-600',
      extraBadges: [
        e.location  ? { text: `📍 ${e.location}`, color: 'bg-slate-100 text-slate-600' } : null,
        e.witnesses ? { text: `👥 ${e.witnesses}`, color: 'bg-slate-100 text-slate-600' } : null,
        e.evidence  ? { text: '📎 Evidence',       color: 'bg-green-100 text-green-700' } : null,
      ].filter(Boolean),
      navTo: '/events',
      flag: e.type === 'violation' || e.type === 'missed',
    })
  }

  // Communications
  for (const c of comms || []) {
    items.push({
      id: `comm-${c.id}`,
      source: 'comm',
      date: c.date,
      time: c.time || null,
      sortKey: `${c.date} ${c.time || '23:59'}`,
      icon: '💬',
      title: TONE_LABEL[c.tone] || 'Communication',
      summary: c.summary,
      borderClass: TONE_BORDER[c.tone] || 'border-l-slate-400',
      badgeText: TONE_LABEL[c.tone] || c.tone,
      badgeColor: TONE_BADGE[c.tone] || 'bg-slate-100 text-slate-600',
      extraBadges: [
        c.type      ? { text: c.type.charAt(0).toUpperCase() + c.type.slice(1), color: 'bg-slate-100 text-slate-600' } : null,
        c.witnesses ? { text: `👥 ${c.witnesses}`, color: 'bg-slate-100 text-slate-600' } : null,
        c.followup  ? { text: '⏰ Follow-up',      color: 'bg-amber-100 text-amber-700' } : null,
      ].filter(Boolean),
      navTo: '/comms',
      flag: c.tone === 'hostile' || c.tone === 'concerning',
    })
  }

  // Order violations
  for (const v of violations || []) {
    items.push({
      id: `vio-${v.id}`,
      source: 'violation',
      date: v.date,
      time: v.time || null,
      sortKey: `${v.date} ${v.time || '23:59'}`,
      icon: '⚠️',
      title: v.type || 'Order Violation',
      summary: v.description,
      borderClass: VIO_BORDER[v.severity] || 'border-l-amber-400',
      badgeText: (v.severity || 'medium').charAt(0).toUpperCase() + (v.severity || 'medium').slice(1),
      badgeColor: VIO_BADGE[v.severity] || 'bg-amber-100 text-amber-700',
      extraBadges: [
        v.order_title  ? { text: `⚖️ ${v.order_title}`,  color: 'bg-brand-100 text-brand-700' } : null,
        v.order_clause ? { text: `§ ${v.order_clause}`,  color: 'bg-slate-100 text-slate-600' } : null,
        v.witnesses    ? { text: `👥 ${v.witnesses}`,    color: 'bg-slate-100 text-slate-600' } : null,
      ].filter(Boolean),
      navTo: '/violations',
      flag: v.severity === 'high' || v.severity === 'critical',
    })
  }

  // Wellbeing check-ins
  for (const w of wellbeingLogs || []) {
    const mood = MOOD_META[w.mood] || { emoji: '😐', badge: 'bg-slate-100 text-slate-600' }
    const childName = kidMap[w.child_id]
    const details = [w.health, w.school, w.notes].filter(Boolean).join(' · ')
    items.push({
      id: `wb-${w.id}`,
      source: 'wellbeing',
      date: w.date,
      time: null,
      sortKey: `${w.date} 12:00`,
      icon: mood.emoji,
      title: `${w.mood.charAt(0).toUpperCase() + w.mood.slice(1)}${childName ? ` · ${childName}` : ''}`,
      summary: details || 'Wellbeing check-in logged',
      borderClass: w.flag ? 'border-l-red-500' : 'border-l-violet-400',
      badgeText: w.mood.charAt(0).toUpperCase() + w.mood.slice(1),
      badgeColor: mood.badge,
      extraBadges: [
        childName ? { text: `👶 ${childName}`,   color: 'bg-violet-100 text-violet-700' } : null,
        w.sleep   ? { text: `Sleep: ${w.sleep}`, color: 'bg-slate-100 text-slate-600'  } : null,
        w.flag    ? { text: '🚩 Court-flagged',  color: 'bg-red-100 text-red-700'      } : null,
      ].filter(Boolean),
      navTo: '/wellbeing',
      flag: !!w.flag,
    })
  }

  // Calendar events
  for (const c of calendarEvents || []) {
    items.push({
      id: `cal-${c.id}`,
      source: 'calendar',
      date: c.start_date,
      time: c.start_time || null,
      sortKey: `${c.start_date} ${c.start_time || '00:00'}`,
      icon: c.type === 'court' ? '⚖️' : '📅',
      title: c.title,
      summary: CAL_LABEL[c.type] || c.type,
      borderClass: c.type === 'court' ? 'border-l-red-500' : 'border-l-brand-500',
      badgeText: CAL_LABEL[c.type] || c.type,
      badgeColor: CAL_BADGE[c.type] || 'bg-slate-100 text-slate-600',
      extraBadges: [
        c.location                           ? { text: `📍 ${c.location}`,  color: 'bg-slate-100 text-slate-600' } : null,
        c.end_date && c.end_date !== c.start_date ? { text: `→ ${c.end_date}`, color: 'bg-slate-100 text-slate-600' } : null,
      ].filter(Boolean),
      navTo: '/calendar',
      flag: c.type === 'court',
    })
  }

  // Court orders (by effective date)
  for (const o of orders || []) {
    const date = o.effective_date || o.created_at?.split('T')[0]
    if (!date) continue
    items.push({
      id: `ord-${o.id}`,
      source: 'order',
      date,
      time: null,
      sortKey: `${date} 00:00`,
      icon: '⚖️',
      title: o.title || 'Court Order',
      summary: [o.jurisdiction, o.type, o.status].filter(Boolean).join(' · ') || 'Court order on file',
      borderClass: 'border-l-emerald-500',
      badgeText: o.status ? o.status.charAt(0).toUpperCase() + o.status.slice(1) : 'Order',
      badgeColor: 'bg-emerald-100 text-emerald-700',
      extraBadges: [
        o.jurisdiction ? { text: `📍 ${o.jurisdiction}`, color: 'bg-slate-100 text-slate-600' } : null,
      ].filter(Boolean),
      navTo: '/orders',
      flag: false,
    })
  }

  return items
}

// ── Timeline item card ────────────────────────────────────────────────────────
function TimelineItem({ item, onNav }) {
  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-xl shadow-sm border-l-4 ${item.borderClass} px-4 py-3 cursor-pointer active:bg-slate-50 dark:active:bg-slate-700`}
      onClick={() => item.navTo && onNav(item.navTo)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <span className="text-base leading-none mt-0.5 shrink-0">{item.icon}</span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-snug">{item.title}</p>
            {item.summary && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{item.summary}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {item.time && (
            <span className="text-[10px] text-slate-400 font-mono">{item.time}</span>
          )}
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${item.badgeColor}`}>
            {item.badgeText}
          </span>
        </div>
      </div>
      {item.extraBadges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {item.extraBadges.map((b, i) => (
            <span key={i} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${b.color}`}>
              {b.text}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function CaseTimeline() {
  const { user, events, comms, violations, wellbeingLogs, calendarEvents, orders, kidProfiles } = useApp()
  const navigate = useNavigate()
  const [sourceFilter, setSourceFilter] = useState('all')
  const [dateRange,    setDateRange]    = useState('all')

  const allItems = useMemo(() =>
    normalize({ events, comms, violations, wellbeingLogs, calendarEvents, orders, kidProfiles }),
    [events, comms, violations, wellbeingLogs, calendarEvents, orders, kidProfiles]
  )

  const filtered = useMemo(() => {
    let items = allItems
    if (sourceFilter !== 'all') items = items.filter(i => i.source === sourceFilter)
    const cutoff = cutoffDate(dateRange)
    if (cutoff) items = items.filter(i => i.date >= cutoff)
    return [...items].sort((a, b) => b.sortKey.localeCompare(a.sortKey))
  }, [allItems, sourceFilter, dateRange])

  const grouped = useMemo(() => {
    const map = new Map()
    for (const item of filtered) {
      const d = item.date || 'unknown'
      if (!map.has(d)) map.set(d, [])
      map.get(d).push(item)
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [filtered])

  const stats = useMemo(() => ({
    total:      filtered.length,
    violations: filtered.filter(i => i.source === 'violation').length,
    hostile:    filtered.filter(i => i.source === 'comm' && (i.flag)).length,
    flagged:    filtered.filter(i => i.flag).length,
  }), [filtered])

  const sourceCounts = useMemo(() => {
    const cutoff = cutoffDate(dateRange)
    let base = allItems
    if (cutoff) base = base.filter(i => i.date >= cutoff)
    const counts = { all: base.length }
    for (const s of SOURCES.slice(1)) counts[s.id] = base.filter(i => i.source === s.id).length
    return counts
  }, [allItems, dateRange])

  function handleExport() {
    const rangeLabel = DATE_RANGES.find(r => r.id === dateRange)?.label || 'All time'
    printCaseTimeline({ userName: user?.name, state: user?.state, items: filtered, rangeLabel })
  }

  const exportBtn = (
    <button
      onClick={handleExport}
      disabled={filtered.length === 0}
      className="tap-target text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/20 text-white disabled:opacity-40"
    >
      📄 PDF
    </button>
  )

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <AppHeader title="Case Timeline" showBack right={exportBtn} />

      <div className="screen no-scrollbar px-4 py-4 space-y-4">

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Total',      value: stats.total,      color: 'text-brand-700'   },
            { label: 'Violations', value: stats.violations,  color: 'text-orange-600' },
            { label: 'Hostile',    value: stats.hostile,     color: 'text-red-600'    },
            { label: 'Flagged',    value: stats.flagged,     color: 'text-red-600'    },
          ].map(s => (
            <div key={s.label} className="card py-3 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Date range pills */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {DATE_RANGES.map(r => (
            <button
              key={r.id}
              onClick={() => setDateRange(r.id)}
              className={`whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                dateRange === r.id
                  ? 'bg-brand-700 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Source filter pills */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {SOURCES.map(s => (
            <button
              key={s.id}
              onClick={() => setSourceFilter(s.id)}
              className={`whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 ${
                sourceFilter === s.id
                  ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
              }`}
            >
              {s.icon} {s.label}
              {sourceCounts[s.id] > 0 && (
                <span className={`text-[10px] font-bold ${sourceFilter === s.id ? 'opacity-70' : 'text-slate-400'}`}>
                  {sourceCounts[s.id]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Timeline feed */}
        {grouped.length === 0 ? (
          <div className="card py-12 text-center">
            <p className="text-3xl mb-2">🗂️</p>
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No entries found</p>
            <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
              Start logging events, communications, and violations to build your case timeline.
            </p>
          </div>
        ) : (
          grouped.map(([date, items]) => (
            <div key={date}>
              <div className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900 py-1.5 mb-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  {dateLabel(date)}
                  <span className="ml-2 text-[10px] font-normal normal-case text-slate-400">
                    {items.length} {items.length === 1 ? 'entry' : 'entries'}
                  </span>
                </p>
              </div>
              <div className="space-y-2">
                {items.map(item => (
                  <TimelineItem key={item.id} item={item} onNav={navigate} />
                ))}
              </div>
            </div>
          ))
        )}

        <div className="pb-2" />
      </div>
    </div>
  )
}
